import { useEffect, useState } from 'react'
import Head from 'next/head'
import Router, { useRouter } from 'next/router'
import PropTypes from 'prop-types'

import Env from 'Consts/env'
import { ScrollToUp, setGoogleAnalytics, setGTM, setGTM_2 } from 'Helpers/utils'

import ImageFallback from 'Components/imagefallback/next_image'
import MaleDailyNavbar from 'Containers/male_daily/navbar'
import Footer from 'Containers/footer'
import MaleDailyContent from 'Containers/male_daily/content'
import MaleDailyContentShimmer from '../../containers/male_daily/content/shimmer'

const MaleDailyPage = ({ isMobile, isError, articleLatest, videos }) => {
  const [loading, setLoading] = useState(false)
  const router = useRouter()

  const device = isMobile ? 'mobile' : 'desktop'

  const refetch = () => {
    router.replace(router.asPath)
  }

  const routeChangeStart = (path) => {
    if (path === router.asPath) setLoading(true)
  }
  const routeChangeEnd = () => {
    setLoading(false)
  }

  useEffect(() => {
    Router.events.on('routeChangeStart', routeChangeStart)
    Router.events.on('routeChangeComplete', routeChangeEnd)
    Router.events.on('routeChangeError', routeChangeEnd)
    return () => {
      Router.events.off('routeChangeStart', routeChangeStart)
      Router.events.off('routeChangeComplete', routeChangeEnd)
      Router.events.off('routeChangeError', routeChangeEnd)
    }
  }, [])
  return (
    <>
      <Head>
        <link
          rel="stylesheet"
          type="text/css"
          charset="UTF-8"
          href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick.min.css"
        />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick-theme.min.css"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Josefin+Sans:wght@400;600;700&family=Poppins:wght@400;500;600&family=Roboto:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <title>Female Daily Editorial - Male Daily</title>

        {!['canary', 'production'].includes(
          process.env.NEXT_PUBLIC_NODE_ENV,
        ) ? (
          <meta name="robots" content="noindex" />
        ) : null}

        {['canary', 'production'].includes(process.env.NEXT_PUBLIC_NODE_ENV) ? (
          <>
            <script dangerouslySetInnerHTML={setGTM()} />
            <script
              type="text/javascript"
              dangerouslySetInnerHTML={setGTM_2()}
            />
          </>
        ) : null}

        {['canary', 'production'].includes(process.env.NEXT_PUBLIC_NODE_ENV) ? (
          <script dangerouslySetInnerHTML={setGoogleAnalytics()} />
        ) : (
          ''
        )}
      </Head>
      <div className="maledaily">
        <MaleDailyNavbar isMobile={isMobile} />

        <div className="maledaily__content">
          {loading && <MaleDailyContentShimmer isMobile={isMobile} />}

          {!loading && isError && (
            <>
              {!isMobile && <div style={{ height: '80px' }} />}
              <div className={`maledaily__error maledaily__error--${device}`}>
                <ImageFallback
                  src={process.env.NEXT_PUBLIC_S3_IMAGE_SERVER_ERROR}
                  width={200}
                  height={200}
                  quality={100}
                  fallbackSrc={process.env.NEXT_PUBLIC_S3_IMAGE_BROKEN}
                  objectFit="contain"
                />
                <div className="maledaily__error-text">
                  <p className="fd-body-lg-bold">Uh Oh!</p>
                  <p className="fd-body-lg-bold">Something went wrong</p>
                </div>
                <div className="maledaily__error-button">
                  <button className="fd-body-lg-bold" onClick={refetch}>
                    Retry
                  </button>
                </div>
              </div>
              <div style={{ height: isMobile ? '80px' : '168px' }} />
            </>
          )}

          {!loading && !isError && (
            <MaleDailyContent
              articles={articleLatest}
              videos={videos}
              isMobile={isMobile}
            />
          )}
        </div>

        <Footer isMobile={isMobile} handleScrollUp={ScrollToUp} />

        <style jsx>
          {`
            .maledaily {
              min-height: 100vh;
              position: relative;
            }

            .maledaily__content {
              overflow-x: hidden;
            }

            .maledaily__error {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: var(--space-24);
              padding: var(--space-24) 0px;
              height: 464px;
              width: 333px;
              margin: auto;
            }

            .maledaily__error--mobile {
              width: 100%;
            }

            .maledaily__error-text {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 8px;
            }

            .maledaily__error-text p {
              margin: 0px;
              text-align: center;
              color: var(--text-default-inverse);
            }

            .maledaily__error-button button {
              text-decoration: none;
              color: var(--text-default-inverse);
              background-color: #0032b1;
              padding: var(--space-12) var(--space-20);
              border-radius: var(--radius-8);
              border: none;
              margin: auto;
              cursor: pointer;
            }

            .maledaily__error-button button:hover,
            .maledaily__error-button button:active {
              background-color: #002d9f;
            }

            .maledaily__error-button button:active {
              transform: translateY(1px);
            }
          `}
        </style>

        <style jsx global>
          {`
            body {
              background-color: #13161a;
              margin: 0px;
            }

            .maledaily .footer,
            .maledaily .footer-taste {
              background-color: #fff;
            }
          `}
        </style>
      </div>
    </>
  )
}

MaleDailyPage.propTypes = {
  isMobile: PropTypes.bool,
  isError: PropTypes.bool,
  videos: PropTypes.any,
  articleLatest: PropTypes.any,
}

export const getServerSideProps = async ({ req }) => {
  let userAgent
  if (req) {
    // if you are on the server and you get a 'req' property from your context
    userAgent = req.headers['user-agent'] // get the user-agent from the headers
  } else {
    userAgent = navigator.userAgent // if you are on the client you can access the navigator from the window object
  }

  const isMobile = Boolean(
    userAgent?.match(
      /Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i,
    ),
  )

  let isError = false

  let articleLatest
  try {
    const query = new URLSearchParams({ limit: 12 })
    const responseFetchArticleLatest = await fetch(
      `${process.env.NEXT_PUBLIC_TAG_BEAUTY}/${process.env.NEXT_PUBLIC_MALE_DAILY_TAG}?${query}`,
      { headers: { ...Env.HEADERS } },
    )
    if (!responseFetchArticleLatest.ok) throw new Error()
    const responseArticleLatest = await responseFetchArticleLatest.json()
    articleLatest = responseArticleLatest ?? []
  } catch (_error) {
    articleLatest = null
    isError = true
  }

  let videos
  try {
    const query = new URLSearchParams({ limit: 5 })
    const responseFetchVideos = await fetch(
      `${process.env.NEXT_PUBLIC_DISCOVER_VIDEO}/tag/${process.env.NEXT_PUBLIC_MALE_DAILY_TAG}?${query}`,
      { headers: { ...Env.HEADERS } },
    )
    if (!responseFetchVideos.ok) throw new Error()
    const responseVideos = await responseFetchVideos.json()
    videos = responseVideos ?? []
  } catch (_error) {
    videos = null
    isError = true
  }

  return {
    props: {
      isMobile,
      videos,
      articleLatest,
      isError,
    },
  }
}

export default MaleDailyPage
